import React, { FC, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import VendNumber from 'vend-number'
import { Elements } from '@stripe/react-stripe-js'
import { ApiRegister, ApiTransaction } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'
import { useCardNotPresentEffectReducer } from '../../hooks/use-card-not-present-effect-reducer/useCardNotPresentEffectReducer'
import { CardCollectionForm } from '../modal-states/CardCollectionForm'
import { ErrorContent } from '../modal-states/ErrorContent'
import { Loading } from '../modal-states/Loading'
import { SwiperError } from '../../helpers/lspay.types'
import { Processing } from '../modal-states/Processing'
import { useTrackLSPAYTiming } from '../../hooks/timeTracking'

interface Props {
  outletId: string
  amount: VendNumber
  currency: string
  retailerPaymentTypeId: string
  register: ApiRegister
  registerSaleId: string
  transactionId: string
  setDisableTerminalPayment: (disableTerminalPayment: boolean) => void
  setDismissible?: (dismissible: boolean) => void
  intentClientSecret?: string
  close: (transaction?: Promise<ApiTransaction>) => void
  onCancelPayment: () => void
  setSwiperError: (swiperError: SwiperError) => void
  hasMoto: boolean
  cancelProcessing?: () => void
}

export const LSPAYCardNotPresentPayment: FC<Props> = ({
  close,
  onCancelPayment,
  outletId,
  amount,
  currency,
  retailerPaymentTypeId,
  register,
  registerSaleId,
  transactionId,
  setDisableTerminalPayment,
  setDismissible,
  intentClientSecret,
  setSwiperError,
  hasMoto,
  cancelProcessing,
}) => {
  const trackTiming = useTrackLSPAYTiming({
    transactionId,
    paymentTypeId: PAYMENT_TYPES.LSPAY,
    isRefund: amount.isNegative(),
  })

  const [state, dispatch] = useCardNotPresentEffectReducer({
    outletId,
    amount,
    currency,
    retailerPaymentTypeId,
    register,
    registerSaleId,
    transactionId,
    intentClientSecret,
    setSwiperError,
    trackTiming,
  })

  useEffect(() => {
    if (state.status === 'done') {
      close(Promise.resolve(state.transaction))
    }
  }, [close, state])

  // We need to cancel any in-progress LSD transaction when exist
  useEffect(() => {
    if (cancelProcessing) {
      cancelProcessing()
    }
  }, [cancelProcessing])

  useEffect(() => {
    if (!setDismissible) return

    setDismissible(
      state.status === 'collectingCardDetails' || state.status === 'error'
    )
  }, [setDismissible, state.status])

  switch (state.status) {
    case 'fetchingCNPConfig':
    case 'creatingIntent':
      return <Loading />
    case 'collectingCardDetails':
    case 'capturing':
      if (state.status === 'capturing' && state.isRetry) {
        return <Processing />
      }

      return (
        <Elements stripe={loadStripe(state.clientKey)}>
          <CardCollectionForm
            setDisableTerminalPayment={setDisableTerminalPayment}
            setDismissible={
              state.status === 'collectingCardDetails'
                ? setDismissible
                : undefined
            }
            close={onCancelPayment}
            clientSecret={state.clientSecret}
            onSuccess={intentId => {
              dispatch({ type: 'COLLECT_CARD_DETAILS_OK', intentId })
            }}
            trackTiming={trackTiming}
            hasMoto={hasMoto}
            registerId={register.id}
          />
        </Elements>
      )
    case 'error':
      return (
        <ErrorContent
          headingType="sub-heading"
          lspayError={state.errorMessage}
          retry={() =>
            dispatch({ type: 'RETRY', previousState: state.previousState })
          }
          close={onCancelPayment}
        />
      )
    case 'done': {
      // Modal should be closed if the state was done.
      // Hence we can just return null
      return null
    }
    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = state
      return _exhaustiveCheck
    }
  }
}
